import { classNames } from "@/utils/classNames";

export interface LeadPanelTabItem<T extends string = string> {
	label: string;
	value: T;
	/** Optional badge; rendered only when > 0 (e.g. "Research 5", "Profiles 9"). */
	count?: number;
}

interface LeadPanelTabsProps<T extends string> {
	tabs: LeadPanelTabItem<T>[];
	activeTab: T;
	onChange: (value: T) => void;
	/**
	 * Stick the strip to the top of the scroll container at this offset (px).
	 * Omit to render it in the normal flow (e.g. the Profile Tracker detail view).
	 */
	stickyTopPx?: number;
}

/**
 * The canonical lead-panel tab strip (Overview | Research | Timeline | Profiles), shared by
 * every lead panel: ContactLeadPanel (Contacts / Global Search / Chrome extension), the list
 * DetailView (Unassigned / Messages) and the Profile Tracker DetailView.
 */
export const LeadPanelTabs = <T extends string>({
	tabs,
	activeTab,
	onChange,
	stickyTopPx,
}: LeadPanelTabsProps<T>) => {
	return (
		<div
			aria-label="Lead panel tabs"
			className={classNames(
				"z-10 flex border-b border-border-light bg-white px-4",
				stickyTopPx === undefined ? "" : "sticky"
			)}
			role="tablist"
			style={stickyTopPx === undefined ? undefined : { top: stickyTopPx }}
		>
			{tabs.map((tab) => (
				<button
					key={tab.value}
					aria-selected={activeTab === tab.value}
					className={classNames(
						"mr-3.5 flex cursor-pointer items-center gap-1.5 border-b-2 py-2.5 text-xs font-semibold last:mr-0",
						activeTab === tab.value
							? "border-blue-action text-blue-action"
							: "border-transparent text-text-tertiary hover:text-text"
					)}
					role="tab"
					type="button"
					onClick={() => onChange(tab.value)}
				>
					{tab.label}
					{tab.count !== undefined && tab.count > 0 && (
						<span className="bg-gray-100 text-gray-600 rounded-full px-1.5 py-0.5 text-[10px] font-medium">
							{tab.count}
						</span>
					)}
				</button>
			))}
		</div>
	);
};
